<template>
  <li
    v-if="cluster"
    class="clusters-list--cluster-item"
    :class="{ 'clusters-list--active': active }"
  >
    <div class="clusters-list--controls">
      <Button
        type="button"
        class="dots-button"
        @click="toggle"
        aria-haspopup="true"
        aria-controls="user_menu"
        outlined
      >
        <IconDots />
      </Button>

      <Menu ref="clusterMenu" id="cluster_menu" :model="items" :popup="true">
        <template #item="{ item }">
          <Button
            v-if="item.label === 'Edit'"
            @click="onEditCluster"
            outlined
            class="p-button-list"
          >
            <IconEdit />
            Edit
          </Button>
          <Button
            v-if="item.label === 'Delete'"
            @click="onRemoveCluster"
            outlined
            class="p-button-list"
          >
            <IconDelete />
            Delete
          </Button>
        </template>
      </Menu>
    </div>
    <div class="clusters-list--title">{{ cluster.name }}</div>
    <div class="clusters-list--count">
      <IconClusterZone />
      {{ cluster.zones?.length ?? 0 }} Zone<template v-if="cluster.zones?.length !== 1">s</template>

      <IconClusterSensor />
      {{ sensorCount }} Sensor<template v-if="sensorCount !== 1">s</template>
    </div>
  </li>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import type { ClusterType } from '@/types/Event';

import { Button, Menu } from 'primevue';

import IconDots from '@/icons/menu/IconDots.svg?component';
import IconEdit from '@/icons/IconEdit.svg?component';
import IconDelete from '@/icons/IconDelete.svg?component';
import IconClusterSensor from '@/icons/IconClusterSensor.svg?component';
import IconClusterZone from '@/icons/IconClusterZone.svg?component';

const cluster = defineModel<ClusterType>('cluster');

const props = withDefaults(
  defineProps<{
    active?: boolean;
    index?: number;
    canEdit?: boolean;
    canRemove?: boolean;
    showTypes?: boolean;
    showCheck?: boolean;
  }>(),
  {
    active: false,
    canEdit: false,
    canRemove: false,
    showTypes: false,
    showCheck: true,
  },
);

const clusterMenu = ref();

const items = ref([{ label: 'Edit' }, { label: 'Delete' }]);

const toggle = (event: MouseEvent) => {
  clusterMenu.value.toggle(event);
};

const sensorCount = computed(() => {
  if (cluster.value) {
    return Object.values(cluster.value.zones).reduce((acc, val) => acc + val.sensors.length, 0);
  }
  return 0;
});

const emit = defineEmits(['onRemoveCluster', 'onEditCluster']);

const onEditCluster = () => {
  emit('onEditCluster', cluster.value, props.index);
};

const onRemoveCluster = () => {
  emit('onRemoveCluster');
};
</script>

<style lang="scss">
.clusters-list--cluster-item {
  @include mixins.flex-center($horiz: flex-start, $wrap: true);
  @include sizing.padding('xs');
  @include mixins.rounded-border();
  background: variables.$vantage-base-primitives-color-border-100;
  flex-basis: calc(50% - 4px);
  flex-shrink: 0;
  min-height: 100px;
  position: relative;

  &.clusters-list--active {
    border-color: variables.$vantage-base-primitives-color-primary-500;
  }

  .clusters-list--controls {
    margin-left: auto;
  }
}

.modal-body-add-cluster .clusters-list--cluster-item .chips {
  border: 0;
  flex-basis: 100%;
  justify-content: flex-start;
  padding: 0;
}

.clusters-list--check {
  @include mixins.flex-center($horiz: flex-start);
  margin-right: auto;
}

.clusters-list--title {
  @include typography.font-body($size: md, $weight: medium);
  color: variables.$vantage-base-primitives-color-primary-500;
  flex-basis: 100%;
  margin-top: auto;
}

.clusters-list--count {
  @include mixins.flex-center($horiz: flex-start, $gap: variables.$vantage-base-sizing-xxxs);
  @include typography.font-body($size: sm, $weight: normal);
  color: variables.$vantage-base-primitives-color-tertiary-400;

  svg {
    color: variables.$vantage-base-primitives-color-tertiary-400;
  }

  svg:not(:first-of-type) {
    margin-left: variables.$vantage-base-sizing-xxxs;
  }
}

.cluster-list--controls-remove {
  position: absolute;
  top: variables.$vantage-base-sizing-xxs;
  right: 0;

  svg {
    margin-right: 0;
  }

  .p-button {
    border: 0;
  }

  .p-button.p-button-contrast:not(:disabled):hover {
    background: transparent;
    border: 0;
  }
}
</style>
